import {MdAccessTime} from 'react-icons/md';
import {cn} from '../../../../../../lib/classname';
import {
  getDaysFromToday,
  getTimeFromDate,
} from '../../../../../../shared/services/dateUtils';
import {
  formatDateToMonthDayZeroSuppress,
  formatDateWithTime,
} from '../../../../../../shared/services/formatDate';

type Props = {
  startDate?: string;
  endDate?: string;
  hasResult: boolean;
};

// 定数定義
const DAYS_FROM_TODAY = {
  TODAY: 0,
  TOMORROW: 1,
  DAY_AFTER_TOMORROW: 2,
} as const;

const DAY_TEXT_MAP = {
  [DAYS_FROM_TODAY.TODAY]: '本日',
  [DAYS_FROM_TODAY.TOMORROW]: '明日',
  [DAYS_FROM_TODAY.DAY_AFTER_TOMORROW]: '明後日',
} as const;

/**
 * 開始時刻と終了時刻から時刻範囲の文字列を生成
 * 開始時刻と終了時刻が同じ場合は単一の時刻のみを返す
 */
const formatTimeRange = (startTime: string, endTime: string): string => {
  if (!startTime && !endTime) return '';
  if (!endTime || startTime === endTime) return startTime;
  return `${startTime} ~ ${endTime}`;
};

/**
 * 今日からの日数に応じた日付テキストを取得
 */
const getDayText = (daysFromToday: number): string => {
  return DAY_TEXT_MAP[daysFromToday as keyof typeof DAY_TEXT_MAP] ?? '明後日';
};

/**
 * 注目表示用のイベント日時フォーマット
 * 「本日」「明日」「明後日」形式で表示
 */
const formatAttentionViewEvent = (
  daysFromToday: number,
  isOneDayEvent: boolean,
  startTime: string,
  endTime: string,
): string => {
  const dayText = getDayText(daysFromToday);
  const timeRange = formatTimeRange(startTime, endTime);

  if (isOneDayEvent && timeRange) {
    return `${dayText} ${timeRange} 開催`;
  }

  return `${dayText}${startTime ? ` ${startTime}` : ''} 開催`;
};

/**
 * 同日開催イベントの日時フォーマット
 * M/d (eee) H:mm ~ H:mm 形式で表示
 */
const formatOneDayEvent = (
  startDate: string,
  startTime: string,
  endTime: string,
  formattedStart: string,
  formattedEnd: string,
): string => {
  const dateOnly = formatDateToMonthDayZeroSuppress(startDate);
  const timeRange = formatTimeRange(startTime, endTime);

  if (timeRange) {
    return `${dateOnly} ${timeRange} 開催`;
  }

  return `${formattedStart || formattedEnd} 開催`;
};

/**
 * 複数日開催イベントの日時フォーマット
 */
const formatMultiDayEvent = (
  formattedStart: string,
  formattedEnd: string,
): string => {
  if (formattedStart && formattedEnd) {
    return `${formattedStart} ~ ${formattedEnd} 開催`;
  }
  if (formattedEnd) {
    return `${formattedEnd} 開催`;
  }
  return `${formattedStart} 開催`;
};

/**
 * 開催日表示コンポーネント
 *
 * 赤文字表示条件: 開始日が1週間以内 かつ 結果未入力
 *
 * 表示パターン:
 * - 開催終了(過去): 「開催終了」(通常色)
 * - 0日後(本日): 「本日 HH:MM 開催」(赤文字)
 * - 1日後(明日): 「明日 HH:MM 開催」(赤文字)
 * - 2日後(明後日): 「明後日 HH:MM 開催」(赤文字)
 * - 3〜7日後: 「MM/DD HH:MM 開催」(赤文字)
 * - 8日後以降 または 結果入力済み: 「MM/DD HH:MM 開催」(通常色)
 */
export const EventDateText: React.FC<Props> = ({
  startDate = '',
  endDate = '',
  hasResult,
}) => {
  // formatDateWithTime は不正な日付の場合に空文字を返す
  const formattedStart = formatDateWithTime(startDate);
  const formattedEnd = formatDateWithTime(endDate);

  if (!formattedStart && !formattedEnd) return null;

  // formattedStart/End が空 = 不正な日付。不正な日付を getDaysFromToday/getTimeFromDate に
  // 渡すと RangeError 等の原因になるため、有効な日付のみを後続の計算に使用する
  const startFromToday = formattedStart ? getDaysFromToday(startDate) : null;
  const endFromToday = formattedEnd ? getDaysFromToday(endDate) : null;
  const isOneDayEvent =
    startFromToday !== null &&
    endFromToday !== null &&
    startFromToday === endFromToday;

  // 赤文字表示: 1週間以内 かつ 結果未入力
  const isAttentionView =
    !hasResult && startFromToday !== null && startFromToday <= 7;
  // 特別テキスト表示: 2日以内の場合は「本日」「明日」「明後日」テキストを使用
  const isSpecialTextView =
    startFromToday !== null &&
    startFromToday <= DAYS_FROM_TODAY.DAY_AFTER_TOMORROW;
  // 開催終了: 開始日または終了日が過去
  const isEventOver =
    !hasResult &&
    ((startFromToday !== null && endFromToday === null && startFromToday < 0) ||
      (endFromToday !== null && endFromToday < 0));

  const formattedStartTime = formattedStart ? getTimeFromDate(startDate) : '';
  const formattedEndTime = formattedEnd ? getTimeFromDate(endDate) : '';

  const getDisplayText = (): string => {
    if (isEventOver) return '開催終了';

    // 注目表示（本日、明日、明後日）
    if (isSpecialTextView && startFromToday !== null) {
      return formatAttentionViewEvent(
        startFromToday,
        isOneDayEvent,
        formattedStartTime,
        formattedEndTime,
      );
    }

    // 同日開催
    if (isOneDayEvent && startDate) {
      return formatOneDayEvent(
        startDate,
        formattedStartTime,
        formattedEndTime,
        formattedStart,
        formattedEnd,
      );
    }

    // 複数日開催
    return formatMultiDayEvent(formattedStart, formattedEnd);
  };

  const displayText = getDisplayText();

  return (
    <div
      className={cn('flex items-center text-black-2 gap-4 text-sm', {
        'text-notification-1': isAttentionView,
      })}
    >
      <MdAccessTime size={12} />
      {displayText}
    </div>
  );
};
